import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as CrossIcon } from "icons/thick-cross-hover.svg";
import { clearSearchAndFilterState } from "slices/discovery/actions/filters";
import { FILTER_TYPES } from "constants/discovery";
import { getCheckedNPs, hasIgnoredDocuments } from "layout/discovery/helpers";
import {
  setFilterDocID,
  setFilterType,
  setFilterView,
} from "slices/discovery/discoverySlice";
import {
  fetchUniqueItemsCount,
  searchItems,
} from "slices/discovery/actions/theme";
import { addToJobQueue } from "slices/app/appSlice";

export const FILTER_NOTES = {
  [FILTER_TYPES.GAPS_RELATIVE_TO_A_REFERENCE_DOC]:
    "Gaps relative to a single doc",
  [FILTER_TYPES.ALIGNMENT_ACROSS_DOCS]: "Full alignment across docs",
  [FILTER_TYPES.PARTIAL_ALIGNMENT_ACROSS_DOCS]: "Partial alignment across docs",
  [FILTER_TYPES.UNIQUE_REFERENCES_ACROSS_DOCS]: "Unique references across docs",
};

const getCurrentNumberOfOccurrences = (
  themes,
  showAll,
  isSearch,
  filterView
) => {
  const shouldApplySpecialCalculation = !showAll || isSearch || filterView;

  if (!shouldApplySpecialCalculation) {
    // Fast path for simple case - just sum occurrences
    let total = 0;
    for (let i = 0; i < themes.length; i++) {
      const theme = themes[i];
      if (!theme.NounPhrase) {
        total += theme.Occurrences;
      }
    }
    return total.toLocaleString();
  }

  // For special calculation, pre-group themes by GroupName to avoid repeated filtering
  const themesByGroup = {};
  for (let i = 0; i < themes.length; i++) {
    const theme = themes[i];
    if (!themesByGroup[theme.GroupName]) {
      themesByGroup[theme.GroupName] = [];
    }
    themesByGroup[theme.GroupName].push(theme);
  }

  // Process each non-NounPhrase theme
  let total = 0;
  for (let i = 0; i < themes.length; i++) {
    const theme = themes[i];
    if (theme.NounPhrase) continue;

    let filteredThemes = themesByGroup[theme.GroupName];

    if (!showAll) {
      filteredThemes = filteredThemes.filter((t) => t.checked || t.selected);
    }
    if (isSearch) {
      filteredThemes = filteredThemes.filter((t) => t.FilteredBySearch);
    }
    if (filterView) {
      filteredThemes = filteredThemes.filter((t) => t.FilteredByFilter);
    }

    if (!filteredThemes.length) {
      continue;
    }

    const group = filteredThemes[0];

    if (!showAll) {
      const nps = filteredThemes.filter((t) => t.NounPhrase);
      const occurrences = nps.reduce((accum, np) => accum + np.Occurrences, 0);
      total += occurrences;
    } else if (isSearch || filterView) {
      total += group.SearchOccurrences;
    } else {
      total += group.Occurrences;
    }
  }

  return total.toLocaleString();
};

const HeaderWidgets = () => {
  const dispatch = useDispatch();

  const allThemes = useSelector((state) => state.discovery.themes);
  const generalTheme = allThemes[0];
  const themes = allThemes.filter((t) => t.GroupName !== "All Themes");
  const isSearch = useSelector((state) => state.discovery.config.isSearch);
  const searchTerm = useSelector((state) => state.discovery.config.searchTerm);
  const filterView = useSelector((state) => state.discovery.config.filterView);
  const filterType = useSelector((state) => state.discovery.config.filterType);
  const showAll = useSelector((state) => state.discovery.config.showAll);
  const uniqueItemCount = useSelector(
    (state) => state.discovery.uniqueItemCount
  );
  const currentUniqueItemCount = useSelector(
    (state) => state.discovery.currentUniqueItemCount
  );

  const normalView =
    !filterView && !isSearch && showAll && !hasIgnoredDocuments(generalTheme);
  const totalNumberOfUniqueItems = uniqueItemCount.toLocaleString();
  const totalNumberOfOccurrences = generalTheme?.Documents.reduce(
    (accum, document) => accum + document.Occurrences,
    0
  ).toLocaleString();

  let currentNumberOfUniqueItems;
  if (showAll) {
    currentNumberOfUniqueItems = currentUniqueItemCount.toLocaleString();
  } else {
    const nps = getCheckedNPs(themes, isSearch, filterView, showAll);
    const npNames = nps.map((np) => np.NounPhrase);
    const uniqueNPs = Array.from(new Set(npNames));
    currentNumberOfUniqueItems = uniqueNPs.length.toLocaleString();
  }

  const currentNumberOfOccurrences = getCurrentNumberOfOccurrences(
    themes,
    showAll,
    isSearch,
    filterView
  );

  const handleRemoveFilter = () => {
    dispatch(addToJobQueue("genericJob"));
    setTimeout(() => {
      dispatch(setFilterView(false));
      dispatch(setFilterType(null));
      dispatch(setFilterDocID(null));

      if (searchTerm) {
        dispatch(searchItems(searchTerm));
      } else {
        dispatch(clearSearchAndFilterState(false));
        dispatch(fetchUniqueItemsCount());
      }
    }, 0);
  };

  const isDataReady = () => {
    if (
      normalView &&
      (totalNumberOfUniqueItems === "0" || totalNumberOfOccurrences === "0")
    ) {
      return false;
    }

    const isTotalNumberOfUniqueItemsZero = totalNumberOfUniqueItems === "0";
    const isTotalNumberOfOccurrencesZero = totalNumberOfOccurrences === "0";
    const isCurrentNumberOfUniqueItemsZero = currentNumberOfUniqueItems === "0";
    const isCurrentNumberOfOccurrencesZero = currentNumberOfOccurrences === "0";

    return !(
      !normalView &&
      (isTotalNumberOfUniqueItemsZero ||
        isTotalNumberOfOccurrencesZero ||
        isCurrentNumberOfUniqueItemsZero ||
        isCurrentNumberOfOccurrencesZero)
    );
  };

  return (
    <div
      data-testid="header-widgets"
      className="categories__header__title-container__widgets"
    >
      {isDataReady() && (
        <>
          {filterView && (
            <div className="categories__header__title-container__widgets__applied_filter">
              {FILTER_NOTES[filterType]}
              <button
                type="button"
                className="categories__header__title-container__widgets__applied_filter__remove_button"
                onClick={handleRemoveFilter}
              >
                <CrossIcon width={8} height={8} />
                <div>Remove</div>
              </button>
            </div>
          )}

          <div className="categories__header__title-container__widgets__counts">
            <div className="categories__header__title-container__widgets__counts__items">
              <div className="categories__header__title-container__widgets__counts__items__currently">
                {normalView
                  ? totalNumberOfUniqueItems
                  : currentNumberOfUniqueItems}
              </div>
              <div className="categories__header__title-container__widgets__counts__items__totaly">
                {normalView ? `Items` : `(${totalNumberOfUniqueItems} Items)`}
              </div>
            </div>

            <div className="categories__header__title-container__widgets__counts__occurrences">
              <div className="categories__header__title-container__widgets__counts__occurrences__currently">
                {normalView
                  ? totalNumberOfOccurrences
                  : currentNumberOfOccurrences}
              </div>
              <div className="categories__header__title-container__widgets__counts__occurrences__totaly">
                {normalView
                  ? "Occurrences"
                  : `(${totalNumberOfOccurrences} Occurrences)`}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HeaderWidgets;
